import { ReactElement } from 'react';
import { notification, Modal } from 'antd';
import styles from './Firewalls.module.less';
import AppEmpty from '../AppEmpty';
import { useParams } from 'react-router-dom';
import useFirewalls from '../../core/hooks/useFirewalls';
import FirewallService from '../../services/FirewallService';
import AppLoading from '../AppLoading';
import CcxIconCheckCircle from '../ccx/icons/CcxIconCheckCircle';
import CcxIconCloseCircleTwoTone from '../ccx/icons/CcxIconCloseCircleTwoTone';
import CcxIconInfoCircleTwoTone from '../ccx/icons/CcxIconInfoCircleTwoTone';
import DeploymentsItem from '../../types/DeploymentsItem';
import FirewallPortsTable from './FirewallsPortsTable';
import useIPAddress from '../../core/hooks/useIpAddress';
import FirewallHeader from './FirewallHeader';
import { FirewallTableInterface } from '../../types/FirewallTable';
import FirewallsTable from './FirewallsTable';

interface UrlProps {
    dataStoreUuid: string;
    testId?: string;
}

interface FirewallsProps {
    testId?: string;
    currentDeployment?: DeploymentsItem | null | undefined;
}

function Firewalls({
    testId = 'Firewalls',
    currentDeployment,
}: FirewallsProps): ReactElement {
    const { dataStoreUuid } = useParams<UrlProps>();
    const { firewalls, refresh, loading } = useFirewalls(dataStoreUuid);
    const { ipAddress } = useIPAddress();

    const onDeleteRule = async (record: FirewallTableInterface) => {
        notification.open({
            message: 'Remove trusted source',
            description: 'The source will be removed soon.',
            icon: <CcxIconInfoCircleTwoTone />,
        });

        Modal.destroyAll();

        try {
            await FirewallService.deleteAllRule({
                rule: record,
                uuid: dataStoreUuid,
            });

            refresh();

            notification.open({
                message: 'Remove trusted source',
                description: 'Source successfully removed',
                icon: <CcxIconCheckCircle twoToneColor="#52c41a" />,
            });
        } catch (e) {
            notification.open({
                message: 'Remove trusted source',
                description: `There was an error removing the source. ${e}`,
                icon: <CcxIconCloseCircleTwoTone twoToneColor="#eb2f96" />,
            });

            console.error(e);
        }
    };

    const onDeleteSelected = async (records: FirewallTableInterface[]) => {
        notification.open({
            message: 'Remove trusted sources',
            description: `Removing ${records.length} source(s).`,
            icon: <CcxIconInfoCircleTwoTone />,
        });

        Modal.destroyAll();

        // The backend splits the source on ',' / ';', so we delete every
        // selected source atomically in a single request. This is safe because
        // each listed rule's `source` is a single CIDR (the list endpoint groups
        // rules by individual IP), so joining never collides with a separator.
        // Removal matches on port/IP/protocol only, so the description is irrelevant.
        try {
            await FirewallService.deleteAllRule({
                rule: {
                    source: records.map((record) => record.source).join(','),
                    description: '',
                },
                uuid: dataStoreUuid,
            });

            refresh();

            notification.open({
                message: 'Remove trusted sources',
                description: `${records.length} source(s) successfully removed`,
                icon: <CcxIconCheckCircle twoToneColor="#52c41a" />,
            });
        } catch (e) {
            notification.open({
                message: 'Remove trusted sources',
                description: `There was an error removing the sources. ${e}`,
                icon: <CcxIconCloseCircleTwoTone twoToneColor="#eb2f96" />,
            });

            console.error(e);
        }
    };

    const rowExpandable = (record: FirewallTableInterface) => {
        return !!record.ports;
    };

    const expandedRowRenderFirewallTable = (record: FirewallTableInterface) => {
        return (
            <FirewallPortsTable
                rule={record}
                ports={record.ports}
                dataStoreUuid={dataStoreUuid}
                refresh={refresh}
            />
        );
    };

    return (
        <section data-testid={testId} className={styles.Firewalls}>
            <FirewallHeader
                ipAddress={ipAddress}
                refresh={refresh}
                dataStoreUuid={dataStoreUuid}
                currentDeployment={currentDeployment}
            />

            {loading ? (
                <AppLoading />
            ) : firewalls?.length ? (
                <div className={styles.FirewallPortsTable}>
                    <FirewallsTable
                        firewalls={firewalls}
                        onDeleteRule={onDeleteRule}
                        onDeleteSelected={onDeleteSelected}
                        expandedRowRender={expandedRowRenderFirewallTable}
                        rowExpandable={rowExpandable}
                    />
                </div>
            ) : (
                <AppEmpty message="There are no firewall rules yet" />
            )}
        </section>
    );
}

export default Firewalls;
